/**
 * Share / export feature module — team upload (share link), social share,
 * export-for-share, and the NLE (FCP7 XML) + HTML export bridges, plus their
 * pending-promise request/response state and outbound senders. Case bodies,
 * senders, module state, and consts moved verbatim from embeddedBridge.ts.
 */

import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative, diagLog } from '../transport';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { useTeamShareProgressStore } from '@/store/teamShareProgressStore.ts';
import type { TeamUploadProgressPayload } from '@/store/teamShareProgressStore.ts';
import type { NLEManifest } from '@/export/nleManifestBuilder.ts';
import type { ExportNLEHooks, ExportNLEPhase } from '@/export/nleExport.ts';

// ─── Message types ───────────────────────────────────────────────────────

export interface TeamAccessStateMessage {
  type: 'teamAccessState';
  payload: {
    hasTeamAccess: boolean;
    teamUrl: string | null;
    password?: string | null;
    sessionId?: string | null;
    revision?: number;
  };
}

export interface TeamShareErrorMessage {
  type: 'teamShareError';
  payload: { error: string };
}

/**
 * Unity's progress heartbeat for an in-flight share (20260924). `stage` is TeamAccessUploader's
 * stage string ("requesting-token", "uploading-project", "uploading-thumbnails",
 * "uploading-headshots", "uploading-videos", "uploading-audio", "deleting-orphans", "finalizing")
 * or one of the two share-preparation stages Unity emits before the upload starts
 * ("preparing-thumbnails", "preparing"). `current`/`total` are 0/0 when the stage has nothing to
 * count. Feeds teamShareProgressStore; TeamShareOverlay renders it.
 */
export interface TeamUploadProgressMessage {
  type: 'teamUploadProgress';
  payload: TeamUploadProgressPayload;
}

export interface SocialShareResultMessage {
  type: 'socialShareResult';
  payload: { completed: boolean; activityType?: string };
}

export interface ExportShareResultMessage {
  type: 'exportShareResult';
  payload: { completed: boolean };
}

export interface ExportNLEProgressMessage {
  type: 'exportNLEProgress';
  payload: { requestId: string; phase: string; current: number; total: number };
}

export interface ExportNLECompleteMessage {
  type: 'exportNLEComplete';
  payload: { requestId: string; success: boolean; error?: string; savedPath?: string };
}

export interface ExportHtmlCompleteMessage {
  type: 'exportHtmlComplete';
  payload: { requestId: string; success: boolean; error?: string };
}

export type ShareMessage =
  | TeamAccessStateMessage
  | TeamShareErrorMessage
  | TeamUploadProgressMessage
  | SocialShareResultMessage
  | ExportShareResultMessage
  | ExportNLEProgressMessage
  | ExportNLECompleteMessage
  | ExportHtmlCompleteMessage;

// ─── Team share state ────────────────────────────────────────────────────

export interface TeamShareResult {
  teamUrl: string;
  password: string | null;
  sessionId: string;
  revision: number;
  /** Audio URLs pre-uploaded by the web app (for direct Supabase patch). */
  _audioUrls?: Array<{ clipId: number; url: string }>;
}

let pendingTeamShare: {
  resolve: (result: TeamShareResult) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
  audioUrls?: Array<{ clipId: number; url: string }>;
} | null = null;

// ─── Social share state ─────────────────────────────────────────────────

let pendingSocialShare: {
  resolve: (result: { completed: boolean; activityType?: string }) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
} | null = null;

// ─── Export-for-share state ─────────────────────────────────────────────

let pendingExportShare: {
  resolve: (result: { completed: boolean }) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
} | null = null;

// ─── Inbound handlers ────────────────────────────────────────────────────

export const shareHandlers = {
  teamAccessState: (msg: TeamAccessStateMessage) => {
    diagLog('Share', `teamAccessState received: hasTeamAccess=${msg.payload.hasTeamAccess}, url=${msg.payload.teamUrl ? 'yes' : 'null'}, rev=${msg.payload.revision}`);
    useProjectStore.getState().setTeamAccessState(
      msg.payload.hasTeamAccess,
      msg.payload.teamUrl,
      msg.payload.sessionId,
      msg.payload.revision,
      msg.payload.password,
    );
    // Eagerly init embedded Supabase client when a review session exists
    if (msg.payload.sessionId && useAccountStore.getState().nativeAccessToken) {
      import('@/api/supabaseClient.ts').then(m => m.getSupabaseForEmbedded());
    }
    // Resolve pending team share promise if present
    if (pendingTeamShare && msg.payload.hasTeamAccess && msg.payload.teamUrl) {
      diagLog('Share', 'teamAccessState resolving pending share promise');
      clearTimeout(pendingTeamShare.timeout);
      const stashedAudio = pendingTeamShare.audioUrls;
      pendingTeamShare.resolve({
        teamUrl: msg.payload.teamUrl,
        password: msg.payload.password ?? null,
        sessionId: msg.payload.sessionId ?? '',
        revision: msg.payload.revision ?? 1,
        _audioUrls: stashedAudio,
      });
      pendingTeamShare = null;
      useTeamShareProgressStore.getState().end();
    }
  },
  teamShareError: (msg: TeamShareErrorMessage) => {
    if (pendingTeamShare) {
      clearTimeout(pendingTeamShare.timeout);
      pendingTeamShare.reject(new Error(msg.payload.error));
      pendingTeamShare = null;
      useTeamShareProgressStore.getState().end();
    }
  },
  teamUploadProgress: (msg: TeamUploadProgressMessage) => {
    // Counters only: the store's `active` bit belongs to the park/settle sites below, so a
    // heartbeat for a share the web did not start cannot raise a scrim nothing will lower.
    useTeamShareProgressStore.getState().report(msg.payload);
  },
  socialShareResult: (msg: SocialShareResultMessage) => {
    if (pendingSocialShare) {
      clearTimeout(pendingSocialShare.timeout);
      pendingSocialShare.resolve(msg.payload);
      pendingSocialShare = null;
    }
  },
  exportShareResult: (msg: ExportShareResultMessage) => {
    if (pendingExportShare) {
      clearTimeout(pendingExportShare.timeout);
      pendingExportShare.resolve(msg.payload);
      pendingExportShare = null;
    }
  },
  exportNLEProgress: (msg: ExportNLEProgressMessage) => {
    const h = _exportNLEHandlers.get(msg.payload.requestId);
    if (h) h.onProgress(msg.payload);
  },
  exportNLEComplete: (msg: ExportNLECompleteMessage) => {
    const h = _exportNLEHandlers.get(msg.payload.requestId);
    if (h) h.onComplete(msg.payload);
  },
  exportHtmlComplete: (msg: ExportHtmlCompleteMessage) => {
    const h = _exportHtmlHandlers.get(msg.payload.requestId);
    if (h) h(msg.payload);
  },
} satisfies HandlerMap;

// ─── Team upload requests ────────────────────────────────────────────────

/**
 * Pre-upload audio blobs from audioBlobStore to Vercel Blob.
 * Returns {clipId, url}[] for clips that were successfully uploaded.
 *
 * This bypasses the fragile bridge-sync → SQLite → extract pipeline.
 * The web app has the canonical audio data in memory; uploading it
 * directly is far more reliable than round-tripping through the bridge.
 */
async function preUploadAudioBlobs(): Promise<Array<{ clipId: number; url: string }>> {
  const t0 = performance.now();
  const { useAudioStore } = await import('@/store/audioStore.ts');
  const { getBlob } = await import('@/previs/audioBlobStore.ts');
  const clips = useAudioStore.getState().audioClips;
  diagLog('Share', `preUploadAudio: audioClips count=${clips.length}, ids=[${clips.map(c => c.id).join(',')}]`);
  if (clips.length === 0) return [];

  // Heal the blob store first. This path does NOT run gatherExportData (the embedded branch of
  // ShareDialog.handleCreate calls requestTeamUpload directly), so nothing else has asked Unity for
  // the bytes of a natively-recorded clip — and the filter below would silently drop it, which is the
  // same silence the HTML export had. Ids already backed by a blob cost nothing.
  const { ensureAudioClipBytes } = await import('./audio.ts');
  await ensureAudioClipBytes(clips.map(c => c.id));

  // Only upload clips that have blob data in memory
  const clipsWithBlobs = clips.filter(c => getBlob(c.id));
  diagLog('Share', `preUploadAudio: clips with blobs=${clipsWithBlobs.length}, ids=[${clipsWithBlobs.map(c => c.id).join(',')}]`);
  if (clipsWithBlobs.length === 0) return [];

  const { getUploadToken } = await import('@/api/publishApi.ts');
  const { uploadToBlob } = await import('@/export/publishOrchestrator.ts');

  diagLog('Share', `preUploadAudio: getting upload token... (${(performance.now() - t0).toFixed(0)}ms)`);
  const { clientToken, pathPrefix } = await getUploadToken();
  diagLog('Share', `preUploadAudio: got token, pathPrefix=${pathPrefix} (${(performance.now() - t0).toFixed(0)}ms)`);
  const results: Array<{ clipId: number; url: string }> = [];

  for (const clip of clipsWithBlobs) {
    const raw = getBlob(clip.id);
    if (!raw || raw.length === 0) continue;

    try {
      const clipStart = performance.now();
      const mimeType = clip.mimeType || 'audio/mpeg';
      const ext = mimeType.includes('wav') ? 'wav' : mimeType.includes('ogg') ? 'ogg' : 'mp3';
      const pathname = `${pathPrefix}audio_${clip.id}.${ext}`;
      diagLog('Share', `preUploadAudio: uploading clip ${clip.id}, size=${raw.length}, mime=${mimeType}`);
      const url = await uploadToBlob(clientToken, pathname, raw, mimeType);
      diagLog('Share', `preUploadAudio: uploaded clip ${clip.id} in ${(performance.now() - clipStart).toFixed(0)}ms`);
      results.push({ clipId: clip.id, url });
    } catch (err) {
      diagLog('Share', `preUploadAudio: FAILED clip ${clip.id}: ${(err as Error).message}`);
    }
  }

  diagLog('Share', `preUploadAudio: done, ${results.length} results in ${(performance.now() - t0).toFixed(0)}ms`);
  return results;
}

/**
 * Ask Unity to upload the project via the team upload pipeline.
 * Returns a promise that resolves when the upload completes (teamAccessState message)
 * or rejects on error (teamShareError message) or 60-second timeout.
 */
export async function requestTeamUpload(ttlDays: number): Promise<TeamShareResult> {
  if (pendingTeamShare) {
    throw new Error('Team upload already in progress');
  }

  const t0 = performance.now();
  diagLog('Share', 'requestTeamUpload START');

  // Pre-upload audio blobs from web app memory → Vercel Blob CDN.
  // This bypasses the bridge-sync → SQLite pipeline which drops audio data.
  let audioUrls: Array<{ clipId: number; url: string }> = [];
  try {
    diagLog('Share', 'audio pre-upload starting...');
    audioUrls = await preUploadAudioBlobs();
    diagLog('Share', `audio pre-upload done: ${audioUrls.length} urls (${(performance.now() - t0).toFixed(0)}ms)`);
  } catch (err) {
    diagLog('Share', `audio pre-upload FAILED: ${(err as Error).message}`);
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      if (pendingTeamShare) {
        pendingTeamShare = null;
        useTeamShareProgressStore.getState().end();
        reject(new Error('Team upload timed out'));
      }
    }, 5 * 60 * 1000);

    pendingTeamShare = { resolve, reject, timeout, audioUrls };
    // The overlay's arming point: from here until the promise settles the web owns the wait
    // state (teamShareProgressStore's header says why Unity no longer does).
    useTeamShareProgressStore.getState().begin();
    diagLog('Share', `sending enableTeamAccess (ttl=${ttlDays}, audioUrls=${audioUrls.length}) at ${(performance.now() - t0).toFixed(0)}ms`);
    enqueueSendToNative('enableTeamAccess', {
      ttlDays,
      audioUrls: audioUrls.length > 0 ? audioUrls : undefined,
    });
  });
}

/**
 * Ask Unity to sync/update the existing team project.
 * Returns a promise that resolves when the update completes.
 */
export async function requestTeamUpdate(): Promise<TeamShareResult> {
  if (pendingTeamShare) {
    throw new Error('Team upload already in progress');
  }

  const t0 = performance.now();
  diagLog('Share', 'requestTeamUpdate START');

  // Pre-upload audio blobs (same as requestTeamUpload)
  let audioUrls: Array<{ clipId: number; url: string }> = [];
  try {
    diagLog('Share', 'audio pre-upload starting...');
    audioUrls = await preUploadAudioBlobs();
    diagLog('Share', `audio pre-upload done: ${audioUrls.length} urls (${(performance.now() - t0).toFixed(0)}ms)`);
  } catch (err) {
    diagLog('Share', `audio pre-upload FAILED: ${(err as Error).message}`);
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      if (pendingTeamShare) {
        pendingTeamShare = null;
        useTeamShareProgressStore.getState().end();
        reject(new Error('Team upload timed out'));
      }
    }, 5 * 60 * 1000);

    pendingTeamShare = { resolve, reject, timeout, audioUrls };
    useTeamShareProgressStore.getState().begin();
    diagLog('Share', `sending updateTeamVersion (audioUrls=${audioUrls.length}) at ${(performance.now() - t0).toFixed(0)}ms`);
    enqueueSendToNative('updateTeamVersion', {
      audioUrls: audioUrls.length > 0 ? audioUrls : undefined,
    });
  });
}

// ─── Social share requests ──────────────────────────────────────────────

/**
 * Ask Unity to open the native share sheet with an image.
 * Returns a promise that resolves when the share completes or is cancelled.
 * Uses chunking for images whose base64 exceeds 512 KB. 2-minute timeout.
 */
export function requestSocialShare(
  imageBase64: string,
  mimeType: string,
  text: string,
): Promise<{ completed: boolean; activityType?: string }> {
  if (pendingSocialShare) {
    return Promise.reject(new Error('Social share already in progress'));
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      if (pendingSocialShare) {
        pendingSocialShare = null;
        reject(new Error('Social share timed out'));
      }
    }, 2 * 60 * 1000);

    pendingSocialShare = { resolve, reject, timeout };

    const CHUNK_THRESHOLD = 512 * 1024;
    if (imageBase64.length <= CHUNK_THRESHOLD) {
      enqueueSendToNative('socialShare', { imageData: imageBase64, mimeType, text });
    } else {
      const CHUNK_SIZE = 512 * 1024;
      const totalChunks = Math.ceil(imageBase64.length / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const chunk = imageBase64.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        enqueueSendToNative('socialShareChunk', {
          mimeType,
          text,
          chunkIndex: i,
          totalChunks,
          data: chunk,
        });
      }
    }
  });
}

/**
 * Ask Unity to open the native export pipeline and share the result.
 * Unity shows the export settings popup, renders with watermarks, then
 * opens the share sheet (skipping the save-or-share dialog on macOS).
 * 60-second timeout.
 */
export function requestExportForShare(
  scope: 'allScenes' | 'currentScene',
): Promise<{ completed: boolean }> {
  if (pendingExportShare) {
    return Promise.reject(new Error('Export share already in progress'));
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      if (pendingExportShare) {
        pendingExportShare = null;
        reject(new Error('Export share timed out'));
      }
    }, 60_000);

    pendingExportShare = { resolve, reject, timeout };
    enqueueSendToNative('exportForShare', { scope });
  });
}

// ─── NLE Export bridge ───────────────────────────────────────────────────

// Per-requestId handler table. Supports a single in-flight NLE export (only
// one can be active at a time from the UI), but the map keeps cleanup tidy
// and would support concurrent exports if that ever becomes needed.
const _exportNLEHandlers: Map<string, {
  onProgress: (msg: { phase: string; current: number; total: number }) => void;
  onComplete: (msg: { success: boolean; error?: string; savedPath?: string }) => void;
}> = new Map();

/**
 * Ships the FCP7 XML and manifest to Unity so it can drive extraction,
 * zipping, and native save/share in embedded mode.
 *
 * Unity responds with zero or more `exportNLEProgress` messages followed
 * by a single `exportNLEComplete`. The caller supplies hooks for progress
 * reporting and cancellation polling.
 *
 * Cancel flow: when `hooks.isCancelled()` returns true we send a single
 * `exportNLECancel` message and keep waiting — Unity will acknowledge with
 * `exportNLEComplete { success: false, error: 'cancelled' }`, which
 * resolves (not rejects) so the caller can distinguish user-cancel from
 * hard errors.
 *
 * Hard timeout: 5 minutes. Rejects with an Error if Unity never responds.
 */
const NLE_EXPORT_HARD_TIMEOUT_MS = 5 * 60 * 1000;

export function bridgeExportNLE(
  xmlString: string,
  manifest: NLEManifest,
  projectName: string,
  hooks: ExportNLEHooks,
): Promise<void> {
  const requestId = `nle_export_${Date.now()}_${Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')}`;

  return new Promise<void>((resolve, reject) => {
    let cancelSent = false;

    // Declared via `let` and assigned below so the closures inside hardTimeoutId
    // and the handler can reference them through the let-binding without TDZ
    // hazards. Both are non-null by the time any callback can fire (synchronous
    // assignment within this Promise executor).
    let cancelIntervalId: ReturnType<typeof setInterval>;
    let hardTimeoutId: ReturnType<typeof setTimeout>;

    function cleanup(): void {
      clearInterval(cancelIntervalId);
      clearTimeout(hardTimeoutId);
      _exportNLEHandlers.delete(requestId);
    }

    // ── Hard timeout — 5 minutes ──────────────────────────────────────────
    hardTimeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Export timed out after ${NLE_EXPORT_HARD_TIMEOUT_MS / 60_000} minutes`));
    }, NLE_EXPORT_HARD_TIMEOUT_MS);

    // ── Register incoming-message handlers ────────────────────────────────
    _exportNLEHandlers.set(requestId, {
      onProgress(msg) {
        hooks.onProgress(msg.phase as ExportNLEPhase, msg.current, msg.total);
      },
      onComplete(msg) {
        cleanup();
        if (msg.success) {
          resolve();
        } else if (msg.error === 'cancelled') {
          // User-initiated cancel: clean exit, not an error
          resolve();
        } else {
          reject(new Error(msg.error ?? 'NLE export failed'));
        }
      },
    });

    // ── Cancellation watcher — 250 ms polling ─────────────────────────────
    cancelIntervalId = setInterval(() => {
      if (!cancelSent && hooks.isCancelled()) {
        cancelSent = true;
        // Tell Unity to cancel. Unity will send exportNLEComplete with error:'cancelled'.
        enqueueSendToNative('exportNLECancel', { requestId });
        // Keep the interval alive so we don't re-send if the response is slow.
        // cleanup() will clear it when the complete message arrives or on timeout.
      }
    }, 250);

    // ── Send the initial export request ───────────────────────────────────
    // NLEManifest's nested typed shapes aren't assignable to Record<string, unknown>
    // without the double cast; sendToNative serializes via JSON.stringify so the
    // structure round-trips correctly to Unity.
    enqueueSendToNative('exportNLE', {
      requestId,
      xmlString,
      manifest: manifest as unknown as Record<string, unknown>,
      projectName,
    });
  });
}

// ─── HTML Export bridge ──────────────────────────────────────────────────

const _exportHtmlHandlers: Map<string, (msg: { success: boolean; error?: string }) => void> = new Map();

const HTML_EXPORT_HARD_TIMEOUT_MS = 5 * 60 * 1000;
const HTML_EXPORT_CHUNK_SIZE = 384 * 1024;

/**
 * Ships an HTML export to Unity for native save/share. The HTML payload is
 * base64-encoded (URL-safe inside a JSON string) and split into ~384 KB
 * chunks so each navigation stays under WKWebView's URL limit.
 *
 * Unity accumulates chunks keyed by requestId, decodes the file once the
 * final chunk arrives, presents the platform-native save panel (macOS) or
 * share sheet (iOS), then sends `exportHtmlComplete`.
 */
export function bridgeExportHtml(
  htmlString: string,
  fileName: string,
  projectName: string,
): Promise<void> {
  const requestId = `html_export_${Date.now()}_${Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')}`;
  const base64 = utf8ToBase64(htmlString);
  const totalChunks = Math.max(Math.ceil(base64.length / HTML_EXPORT_CHUNK_SIZE), 1);

  return new Promise<void>((resolve, reject) => {
    const hardTimeoutId = setTimeout(() => {
      _exportHtmlHandlers.delete(requestId);
      reject(new Error(`HTML export timed out after ${HTML_EXPORT_HARD_TIMEOUT_MS / 60_000} minutes`));
    }, HTML_EXPORT_HARD_TIMEOUT_MS);

    _exportHtmlHandlers.set(requestId, (msg) => {
      clearTimeout(hardTimeoutId);
      _exportHtmlHandlers.delete(requestId);
      if (msg.success) resolve();
      else reject(new Error(msg.error ?? 'HTML export failed'));
    });

    for (let i = 0; i < totalChunks; i++) {
      const chunk = base64.substring(i * HTML_EXPORT_CHUNK_SIZE, (i + 1) * HTML_EXPORT_CHUNK_SIZE);
      const payload: Record<string, unknown> = {
        requestId,
        chunkIndex: i,
        totalChunks,
        data: chunk,
      };
      if (i === 0) {
        payload.fileName = fileName;
        payload.projectName = projectName;
      }
      enqueueSendToNative('exportHtmlChunk', payload);
    }
  });
}

/** Encode a UTF-8 string as base64. Preserves non-ASCII characters that
 *  the legacy `btoa()` rejects. */
function utf8ToBase64(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}
